/**
 * File-based cache for workflow model discovery results and user selection.
 *
 * A single cache file is stored at
 * `~/.cache/opencode/gitlab-workflow-model-cache.json`
 * (or `$XDG_CACHE_HOME/opencode/...` when set).
 *
 * The file contains a JSON object keyed by a truncated SHA-256 hash of the
 * workspace directory path combined with the GitLab instance URL. This ensures
 * that switching instances for the same workspace invalidates the cache.
 * Each value holds:
 *   - `discovery`: The full DiscoveredModels JSON from the last successful discovery
 *   - `selectedModelRef`: The model ref the user last selected
 *   - `selectedModelName`: Human-readable name of the selected model
 *   - `updatedAt`: ISO timestamp of the last write
 */

import * as fs from 'fs';
import * as path from 'path';
import * as os from 'os';
import * as crypto from 'crypto';
import type { DiscoveredModels } from './gitlab-model-discovery';

export interface ModelCacheEntry {
  discovery: DiscoveredModels | null;
  selectedModelRef: string | null;
  selectedModelName: string | null;
  updatedAt: string;
}

type CacheFile = Record<string, ModelCacheEntry>;

function getCacheFilePath(): string {
  const cacheHome = process.env.XDG_CACHE_HOME || path.join(os.homedir(), '.cache');
  return path.join(cacheHome, 'opencode', 'gitlab-workflow-model-cache.json');
}

function computeCacheKey(workDir: string, instanceUrl?: string): string {
  const normalizedUrl = (instanceUrl || 'https://gitlab.com').replace(/\/$/, '');
  return crypto
    .createHash('sha256')
    .update(`${workDir}\0${normalizedUrl}`)
    .digest('hex')
    .slice(0, 12);
}

export class GitLabModelCache {
  private readonly filePath: string;
  private readonly key: string;

  constructor(workDir: string, instanceUrl?: string) {
    this.filePath = getCacheFilePath();
    this.key = computeCacheKey(workDir, instanceUrl);
  }

  private readAll(): CacheFile {
    try {
      if (!fs.existsSync(this.filePath)) {
        return {};
      }
      const raw = fs.readFileSync(this.filePath, 'utf-8');
      return JSON.parse(raw) as CacheFile;
    } catch {
      return {};
    }
  }

  private writeAll(data: CacheFile): void {
    try {
      const dir = path.dirname(this.filePath);
      fs.mkdirSync(dir, { recursive: true, mode: 0o700 });
      fs.writeFileSync(this.filePath, JSON.stringify(data, null, 2), { mode: 0o600 });
    } catch {
      // Best-effort — cache write failures are non-fatal
    }
  }

  /**
   * Load the cached entry for this workspace.
   * Returns null if no cache exists or is unreadable.
   */
  load(): ModelCacheEntry | null {
    return this.readAll()[this.key] ?? null;
  }

  /**
   * Persist the full cache entry to disk.
   */
  save(entry: ModelCacheEntry): void {
    const data = this.readAll();
    data[this.key] = entry;
    this.writeAll(data);
  }

  /**
   * Update only the discovery portion of the cache, preserving selection.
   */
  saveDiscovery(discovery: DiscoveredModels): void {
    const existing = this.load();
    this.save({
      discovery,
      selectedModelRef: existing?.selectedModelRef ?? null,
      selectedModelName: existing?.selectedModelName ?? null,
      updatedAt: new Date().toISOString(),
    });
  }

  /**
   * Update only the selected model, preserving the discovery data.
   */
  saveSelection(ref: string | null, name: string | null): void {
    const existing = this.load();
    this.save({
      discovery: existing?.discovery ?? null,
      selectedModelRef: ref,
      selectedModelName: name,
      updatedAt: new Date().toISOString(),
    });
  }

  /**
   * Remove the entry for this workspace from the cache file.
   */
  clear(): void {
    const data = this.readAll();
    delete data[this.key];
    if (Object.keys(data).length === 0) {
      try {
        if (fs.existsSync(this.filePath)) {
          fs.unlinkSync(this.filePath);
        }
      } catch {
        // Best-effort
      }
    } else {
      this.writeAll(data);
    }
  }

  /**
   * Convenience: get the cached selected model ref (or null).
   */
  getSelectedModelRef(): string | null {
    return this.load()?.selectedModelRef ?? null;
  }

  /**
   * Convenience: get the cached selected model name (or null).
   */
  getSelectedModelName(): string | null {
    return this.load()?.selectedModelName ?? null;
  }

  /**
   * Convenience: get the cached discovery result (or null).
   */
  getDiscovery(): DiscoveredModels | null {
    return this.load()?.discovery ?? null;
  }
}
